"use client";

import React, { forwardRef, useState } from "react";
import clsx from "clsx";

export type TextFieldState =
  | "default"
  | "hovered"
  | "selected"
  | "error"
  | "disabled";

interface TextFieldProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "size" | "value" | "defaultValue"
  > {
  label?: string;
  required?: boolean;
  errorMessage?: string;
  helperText?: string;
  state?: TextFieldState;
  value?: string;
  defaultValue?: string;
  /** Show the textfield as a dropdown trigger (renders chevron icon and adds onIconClick). */
  dropdown?: boolean;
  /** Custom trailing icon. If `dropdown` is true a chevron is rendered by default. */
  icon?: React.ReactNode;
  onIconClick?: () => void;
  containerClassName?: string;
}

const ChevronDown = ({ flip }: { flip?: boolean }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={clsx("transition-transform duration-200", flip && "rotate-180")}
  >
    <path
      d="M18 9.00005C18 9.00005 13.5811 15 12 15C10.4188 15 6 9 6 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      required,
      errorMessage,
      helperText,
      state,
      value,
      defaultValue,
      dropdown,
      icon,
      onIconClick,
      containerClassName,
      className,
      placeholder = "Input Text",
      disabled,
      readOnly,
      // Only the handlers we wrap are pulled out of the spread; onChange stays in
      // `inputProps` so a consumer's handler (e.g. react-hook-form's register)
      // reaches the input untouched.
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      ...inputProps
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);

    const isControlled = value !== undefined;

    const resolvedState: TextFieldState =
      state ??
      (disabled
        ? "disabled"
        : errorMessage
        ? "error"
        : focused
        ? "selected"
        : hovered
        ? "hovered"
        : "default");

    const borderClass =
      resolvedState === "selected"
        ? "border-b-[1.5px] border-secondary-500"
        : resolvedState === "error"
        ? "border-b border-primary-600"
        : resolvedState === "disabled"
        ? "border-b border-neutral-600"
        : resolvedState === "hovered"
        ? "border-b border-neutral-200"
        : "border-b border-neutral-500";

    // Figma splits the field by "Had value?", and the two branches differ in
    // weight and line-height as well as colour:
    //   empty  = Body 1       (Avenir Regular, 400 / 28px) on neutral/500
    //   filled = Body 1 Heavy (Avenir Demi,    600 / 24px) on text/default
    // The split is driven off :placeholder-shown rather than a React copy of the
    // value, so it also holds for text the browser writes without an onChange
    // event — autofill, and react-hook-form's reset() after a successful submit.
    // (Empty is the base style: with placeholder="" a browser that skips the
    // pseudo-class falls back to the filled branch, which renders nothing.)
    // Colour pairs are spelled out in full: Tailwind only sees class names that
    // appear literally in the source, so they can't be built by interpolation.
    const inputValueClass = clsx(
      "font-[var(--font-avenir)] font-normal leading-7",
      "not-placeholder-shown:font-semibold not-placeholder-shown:leading-6",
      resolvedState === "disabled"
        ? "text-neutral-600 not-placeholder-shown:text-neutral-400"
        : "text-neutral-500 not-placeholder-shown:text-text-default"
    );

    // Trailing chevron tracks the value text (#d6d6d6 filled / #78797d empty) via
    // currentColor on the wrapper. Figma leaves it un-dimmed in the disabled
    // variants; we dim it with the text so a disabled field reads as one block.
    const iconColorClass =
      resolvedState === "disabled"
        ? "text-neutral-600 has-[input:not(:placeholder-shown)]:text-neutral-400"
        : "text-neutral-500 has-[input:not(:placeholder-shown)]:text-text-default";

    const labelColor =
      resolvedState === "disabled" ? "text-neutral-600" : "text-neutral-300";

    const showTrailingIcon = dropdown || !!icon;
    const trailingIconNode =
      icon ?? (dropdown ? <ChevronDown flip={resolvedState === "selected"} /> : null);

    return (
      <div
        className={clsx(
          "inline-flex flex-col items-start w-full",
          containerClassName
        )}
      >
        {label && (
          <div
            className={clsx(
              "flex items-center px-4 leading-7 text-[16px] tracking-[0.5px]",
              "font-[var(--font-avenir)]",
              labelColor
            )}
          >
            <span>{label}</span>
            {required && <span>*</span>}
          </div>
        )}

        <div
          className={clsx(
            // `mx-4 self-stretch` rather than `w-full px-4`: the underline lives on
            // this element, so padding would run it 16px past every piece of text
            // in the form (label, value, error line — all on a 16px inset grid).
            // Margin insets the line itself so it starts where the label starts.
            "flex items-center self-stretch mx-4 h-10 relative transition-colors",
            borderClass,
            iconColorClass
          )}
          onMouseEnter={(e) => {
            setHovered(true);
            onMouseEnter?.(e as unknown as React.MouseEvent<HTMLInputElement>);
          }}
          onMouseLeave={(e) => {
            setHovered(false);
            onMouseLeave?.(e as unknown as React.MouseEvent<HTMLInputElement>);
          }}
        >
          <input
            ref={ref}
            value={value}
            defaultValue={isControlled ? undefined : defaultValue}
            disabled={disabled || resolvedState === "disabled"}
            readOnly={readOnly}
            placeholder={placeholder}
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
            className={clsx(
              "flex-1 min-w-0 bg-transparent outline-none border-none text-[16px] tracking-[0.5px]",
              "placeholder:text-current",
              inputValueClass,
              disabled && "cursor-not-allowed",
              className
            )}
            {...inputProps}
          />
          {showTrailingIcon && (
            <button
              type="button"
              tabIndex={-1}
              onClick={onIconClick}
              disabled={disabled || resolvedState === "disabled"}
              className={clsx(
                "shrink-0 size-6 inline-flex items-center justify-center",
                onIconClick ? "cursor-pointer" : "cursor-default"
              )}
              aria-label="Toggle"
            >
              {trailingIconNode}
            </button>
          )}
        </div>

        {resolvedState === "error" && errorMessage && (
          <div className="flex items-center px-4 mt-2">
            <p className="text-primary-600 text-[12px] leading-5 tracking-[0.25px] font-[var(--font-avenir)] whitespace-nowrap">
              {errorMessage}
            </p>
          </div>
        )}
        {resolvedState !== "error" && helperText && (
          <div className="flex items-center px-4 mt-2">
            <p className="text-neutral-300 text-[12px] leading-5 tracking-[0.25px] font-[var(--font-avenir)]">
              {helperText}
            </p>
          </div>
        )}
      </div>
    );
  }
);

TextField.displayName = "TextField";

export default TextField;
export { TextField };
